import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { axiosRequest } from '../utils/axiosRequest';
import { retrieveItem } from '../utils/helpers';
import {
  ITranscriptSentence,
  ISpeakerTranscript,
  IFilterTranscriptResultsStore,
} from '../global/interfaces';

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const useCopilot = (
  bookingId: string | undefined = '',
  searchEnqueue: (state: ITranscriptSentence) => void = () => {},
) => {
  const queryClient = useQueryClient();
  const axios = axiosRequest();

  const [aiMode, setAiMode] = useState<string>('Filter');
  const [scoreThreshold, setScoreThreshold] = useState<number>(0.5);
  const [filterModel, setFilterModel] = useState<number>(0);
  const filterModelList: string[] = [
    'all-mpnet-base-v2',
    'claude-3-haiku',
    'llama3.1',
    'llama3.2',
    'llama3.2:1b',
    'gemma2',
    'gemma2:2b',
    'mistral',
  ];

  const transcriptSentenceMapRef = useRef<Map<string, ITranscriptSentence>>(
    new Map(),
  ); //this map stores all the transcript sentences from copilotContext as mergedParts updates, format: [{sid: text}...]
  const [speakerTranscript, setSpeakerTranscript] = useState<
    ISpeakerTranscript | undefined
  >();

  const [modelSelect, setModelSelect] = useState<number>(0);
  const modelList: string[] = [
    'haiku-3',
    'sonnet-3.5',
    'gpt-4o',
    'gpt-4o-mini',
  ];

  /*

    Send transcript sentences to server to store embeddings
    - use server side stored embeddings to perform rapid semantic search based on cosine similarity
    - use sentence embeddings for semantic chunking of transcript consecutive sentences
    - storeTranscriptEmbeddingsQueue is sent to server on session loading then incrementally as the transcripts streams in
  
  */
  const transcriptSentenceEmbeddingsQueueMapRef = useRef<Map<string, number>>(
    new Map(),
  );
  const [storeTranscriptEmbeddingsQueue, setStoreTranscriptEmbeddingsQueue] =
    useState<ITranscriptSentence[]>([]);
  const [
    storeTranscriptEmbeddingsLoading,
    setStoreTranscriptEmbeddingsLoading,
  ] = useState<boolean>(false);

  const storeTranscriptSentenceEmbeddings = async (
    storeTranscriptEmbeddingsQueue: ITranscriptSentence[] = [],
  ) => {
    console.log(
      `:::::do storeTranscriptSentenceEmbeddings api request, filterModel: ${filterModelList[filterModel]}`,
    );

    if (
      !storeTranscriptEmbeddingsQueue.length ||
      storeTranscriptEmbeddingsLoading
    )
      return;
    console.log(
      'storeTranscriptEmbeddingsQueue:',
      storeTranscriptEmbeddingsQueue,
    );

    setStoreTranscriptEmbeddingsLoading(true);
    const { data: response } = await axios.post(
      `/ai/store_transcript_embeddings/${bookingId}`,
      {
        sentences: storeTranscriptEmbeddingsQueue,
        model: filterModelList[filterModel],
      },
    );

    console.log(
      'storeTranscriptSentenceEmbeddings response data:',
      response?.data,
    );
    await sleep(1000);

    const storeTranscriptEmbeddingsMap = new Map<string, ITranscriptSentence>();
    let lastSid = '';
    if (response?.data.sids) {
      storeTranscriptEmbeddingsQueue.forEach((item) => {
        storeTranscriptEmbeddingsMap.set(item.sid, item);
      });

      response.data.sids.forEach((sid: string) => {
        lastSid = sid;
        transcriptSentenceEmbeddingsQueueMapRef.current.set(sid, 1);
        storeTranscriptEmbeddingsMap.delete(sid);
      });

      if (filterTranscriptPrompt) setUpdateFilterTranscriptQueue(true);
      if (applyClassifier.length) setUpdateClassifySentencesQueue(true);
      /*
      //this will perform retreival on last sentence in transcript as it streams in
      if (searchResults) {
        const lastSentence = storeTranscriptEmbeddingsQueue.find(item => item.sid === lastSid)
        if (lastSentence) searchEnqueue(lastSentence)
      }
*/
    }

    setStoreTranscriptEmbeddingsLoading(false);
    setStoreTranscriptEmbeddingsQueue(
      Array.from(storeTranscriptEmbeddingsMap.values()),
    );

    return response.data;
  };

  const { isLoading, isError: storeTranscriptEmbeddingsError } = useQuery(
    [
      'store_transcript_embeddings',
      storeTranscriptEmbeddingsQueue,
      filterModel,
    ],
    () => storeTranscriptSentenceEmbeddings(storeTranscriptEmbeddingsQueue),
    {
      refetchOnWindowFocus: false,
      enabled: storeTranscriptEmbeddingsQueue.length > 0,
      retry: 0,
    },
  );

  /*

    Transcript filters

  */
  const [updateFilterTranscriptQueue, setUpdateFilterTranscriptQueue] =
    useState<boolean>(false); //need to revisit purpose for this state variable, seems redundant, may not be required
  const [filterTranscriptPrompt, setFilterTranscriptPrompt] =
    useState<string>('');
  const [filterTranscriptContext, setFilterTranscriptContext] =
    useState<string>(
      'A homeowner is describing a problem they have with the laminate floors in their home to a home inspector.',
    );
  const [isFilteringTranscript, setIsFilteringTranscript] = useState<number>(0);
  const [errorFilteringTranscript, setErrorFilteringTranscript] =
    useState<boolean>(false);
  const [filterTranscriptResultsMap, setFilterTranscriptResultsMap] = useState<
    Map<string, number>
  >(new Map());

  /*

    SEMANTIC SEARCH CLASSIFICATION: 
    Send a 'Filter similar statements' value to the server and retrieve similarity scores for all sentences in transcript - filtering
    - create embeddings of 'filter statement' on the php server
    - perform semantic search (cosine similarity) on all stored sentence embeddings
    - return score for each sentence (sid)

  */

  const filterTranscriptWithEncoder = async () => {
    if (
      !updateFilterTranscriptQueue ||
      !filterTranscriptPrompt ||
      filterModel > 0
    )
      return;

    console.log(
      '\nfilterTranscriptRequest updateFilterTranscriptQueue:',
      updateFilterTranscriptQueue,
      'filterTranscriptPrompt:',
      filterTranscriptPrompt,
      'filterTranscriptResultsMap?.size:',
      filterTranscriptResultsMap?.size,
      'filterModel: ' + filterModel,
    );

    const sids: string[] = [];
    //    if (filterTranscriptResultsMap.size) {
    //      transcriptSentenceEmbeddingsQueueMapRef.current.forEach((value, sid) => {
    //        if (!filterTranscriptResultsMap.has(sid)) sids.push(sid)
    //      })

    //      console.log('setFilterSentencesQueue:', sids)
    //      if (!sids.length) return
    //    }

    //await sleep(3000)

    console.log('transcriptSentenceMapRef: ', transcriptSentenceMapRef);

    const sentences: Record<string, string> = {};
    transcriptSentenceMapRef.current.forEach((value, key) => {
      sentences[key] = value.text;
    });

    const { data } = await axios.post(`/ai/filter_transcript/${bookingId}`, {
      ...speakerTranscript,
      context: '',
      prompt: `${filterTranscriptPrompt}`,
      sids,
      model: filterModelList[filterModel],
      sentences,
    });

    if (data.data) {
      console.log('filterTranscriptWithEncoderResults: ', data.data);

      const keys = Object.keys(data.data.scores);
      keys.forEach((key) => {
        filterTranscriptResultsMap.set(key, data.data.scores[key]);
      });
      setFilterTranscriptResultsMap(filterTranscriptResultsMap);

      console.log('filterTranscriptResultsMap: ', filterTranscriptResultsMap);

      if (updateFilterTranscriptQueue) setUpdateFilterTranscriptQueue(false);
    }

    return data.data;
  };

  const {
    data: filterTranscriptResult,
    isLoading: filterTranscriptLoading,
    isError: filterTranscriptError,
  } = useQuery(
    [
      'filter_transcript',
      updateFilterTranscriptQueue,
      filterTranscriptPrompt,
      bookingId,
    ],
    () => filterTranscriptWithEncoder(),
    {
      refetchOnWindowFocus: false,
      enabled: !!filterTranscriptPrompt,
      retry: 0,
    },
  );

  useEffect(() => {
    setIsFilteringTranscript(filterTranscriptLoading ? 1 : 0);
  }, [filterTranscriptLoading]);

  useEffect(() => {
    setErrorFilteringTranscript(filterTranscriptError);
  }, [filterTranscriptError]);

  useEffect(() => {
    console.log('reset setFilterTranscriptResultsMap...');

    setFilterTranscriptResultsMap(new Map());
    setUpdateFilterTranscriptQueue(true);
    queryClient.invalidateQueries('filter_transcript');
  }, [filterTranscriptPrompt, filterTranscriptContext, filterModel]);

  /*

    ZERO-SHOT CLASSIFICATION: 
    Send a 'Filter prompt' and context description to the gen ai server and test if each sentence meet the these conditions - filtering
    - 'filter statement' is a prompt applied to each transcript sentence group, return probability of response = true
    - replace ollama server with custom php server, claude uses Hiaku

  */

  const filterTranscriptWithDecoder = async () => {
    if (isFilteringTranscript || !speakerTranscript) return;

    const startTime = Date.now();

    try {
      setErrorFilteringTranscript(false);
      setIsFilteringTranscript(
        Math.round((1 / speakerTranscript.segments.length) * 100),
      );

      console.log('clear setFilterTranscriptResultsMap...');
      console.log(
        `do filterTranscriptPrompt api request with prompt: ${filterTranscriptPrompt}`,
      );
      //await sleep(3000)

      const _filterTranscriptResultsMap = new Map();
      setFilterTranscriptResultsMap(_filterTranscriptResultsMap);

      const sentences: Record<string, string> = {};
      transcriptSentenceMapRef.current.forEach((value, key) => {
        //only include sentences that are referred to in speakerTranscript
        sentences[key] = value.text;
      });

      console.log('sentences', sentences);
      console.log('speakerTranscript: ', speakerTranscript);

      /*
restructure this code, prepare whole prompt and sentences separately, remove context, sids (should only include new sentences to test)
prompt = const prompt = ``
  - need to add sentence dynamically on server side
  - only include sentences in speakerTranscript.sentences side



*/

      const token = await retrieveItem('hp_auth_token');
      const res = await fetch(
        `${window._env_?.API_SERVER_URL || process.env.REACT_APP_API_SERVER_URL}/ai/filter_transcript_chunks/${bookingId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'text/plain',
          },
          body: JSON.stringify({
            ...speakerTranscript,
            prompt: `In the context of '${filterTranscriptContext}', simply answer yes or no if the condition: '${filterTranscriptPrompt}' is met in the following text: '\${sentence}'`,
            model: filterModelList[filterModel],
            sentences,
          }),
        },
      );

      if (!res.ok) {
        console.error(`HTTP error! status: ${res.status} - ${res.statusText}`);
        setErrorFilteringTranscript(true);
      }

      const reader = res?.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder('utf-8');

      let count = 0;
      while (true) {
        const { value, done } = await reader.read();

        if (value) {
          const chunk = JSON.parse(
            decoder.decode(value, { stream: true }).replace('<<END>>', ''),
          );
          console.log('Received chunk:', typeof chunk, chunk);

          const keys: string[] = Object.keys(chunk);
          keys.forEach((key) => {
            _filterTranscriptResultsMap.set(key, chunk[key]);
          });

          if (keys.length) {
            setFilterTranscriptResultsMap(new Map(_filterTranscriptResultsMap));
          }
          setIsFilteringTranscript(
            Math.round((++count / speakerTranscript.segments.length) * 100),
          );
        }

        if (done) break;
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorFilteringTranscript(true);
    } finally {
      setIsFilteringTranscript(0);
      console.log(
        `Time to complete filter task: ${((Date.now() - startTime) / 1000).toFixed(2)} seconds`,
      );
    }
  };

  const [filterTranscriptResultsStore, setFilterTranscriptResultsStore] =
    useState<IFilterTranscriptResultsStore[]>([]); //need to store in db for this session
  const storeFilterResults = () => {
    console.log('store filter results...');

    const results: { [key: string]: number } = {};
    filterTranscriptResultsMap.forEach((value, key) => {
      results[key] = value;
    });

    filterTranscriptResultsStore.push({
      model: filterModelList[filterModel],
      prompt: filterTranscriptPrompt,
      context: filterTranscriptContext,
      results,
    });

    setFilterTranscriptResultsStore([...filterTranscriptResultsStore]);
  };

  /*

    Send a classifier_name to the server and retrieve classifier results and sid:valuew name pairs

   */
  const [filterHomeSystems, setFilterHomeSystems] = useState<boolean>(false);
  const [updateClassifySentencesQueue, setUpdateClassifySentencesQueue] =
    useState<boolean>(false);
  const classifyTranscriptResultsMapRef = useRef<Map<string, number>>(
    new Map(),
  );
  const homeSystemOptions = [
    '',
    'roofing',
    'exterior',
    'structure',
    'electrical',
    'heating',
    'a/c',
    'insulation',
    'plumbing',
    'interior',
    'appliances',
  ];
  const [selectedClassifierOptions, setSelectedClassifierOptions] = useState<
    number[]
  >([]);
  const [applyClassifier, setApplyClassifier] = useState<string[]>([]);
  const classifyTranscriptRequest = async () => {
    console.log('1...');
    console.log('updateClassifySentencesQueue:' + updateClassifySentencesQueue);
    console.log('applyClassifier?.length: ' + applyClassifier?.length);
    console.log(
      'classifyTranscriptResultsMapRef?.current: ',
      classifyTranscriptResultsMapRef?.current,
    );

    if (
      !updateClassifySentencesQueue ||
      !applyClassifier?.length ||
      !classifyTranscriptResultsMapRef?.current
    )
      return;
    console.log('2...');

    const sids: string[] = [];
    if (classifyTranscriptResultsMapRef.current.size) {
      transcriptSentenceEmbeddingsQueueMapRef.current.forEach((value, sid) => {
        if (
          applyClassifier &&
          !classifyTranscriptResultsMapRef.current.has(sid)
        )
          sids.push(sid);
      });

      console.log('setClassifierSentencesQueue:', sids);
      console.log('3...', sids);
      if (!sids.length) return;
    }

    console.log(
      `do classifyTranscript api request with query: ${applyClassifier}`,
    );
    //await sleep(3000)

    const { data } = await axios.post(`/ai/classify_transcript/${bookingId}`, {
      classifier_models: applyClassifier,
      sids,
    });

    if (data.data) {
      console.log('classifyTranscriptResults: ', data.data);

      const keys = Object.keys(data.data.scores);
      keys.forEach((key) => {
        classifyTranscriptResultsMapRef.current.set(key, data.data.scores[key]);
      });

      setUpdateClassifySentencesQueue(false);
    }

    return data.data;
  };

  const {
    data: classifyTranscriptResult,
    isLoading: classifyTranscriptLoading,
    isError: classifyTranscriptError,
  } = useQuery(
    ['classify_transcript', updateClassifySentencesQueue],
    () => classifyTranscriptRequest(),
    {
      refetchOnWindowFocus: false,
      enabled: applyClassifier.length > 0,
      retry: 0,
    },
  );

  useEffect(() => {
    classifyTranscriptResultsMapRef.current = new Map();
    setUpdateClassifySentencesQueue(true);
  }, [applyClassifier]);

  /*

  make request for ai image description

*/

  interface IAnalyzeImageParams {
    image: string;
    query: string;
    model: string;
  }

  const { mutateAsync: analyzeImage } = useMutation<
    string,
    Error,
    IAnalyzeImageParams
  >(async ({ image, query, model }: IAnalyzeImageParams) => {
    const { data } = await axios.post(`ai/analyze_image/`, {
      query,
      model,
      image,
    });

    if (data.status === 'success') {
      return data.response;
    } else {
      throw new Error('An error occurred while connecting client to room');
    }
  });

  //1024x1024 for Haiku
  async function getImageAsBase64(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to create canvas context'));
          return;
        }

        const maxDim = 1024;
        let { width, height } = img;
        if (width > height && width > maxDim) {
          height *= maxDim / width;
          width = maxDim;
        } else if (height > width && height > maxDim) {
          width *= maxDim / height;
          height = maxDim;
        }

        canvas.width = Math.round(width);
        canvas.height = Math.round(height);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // const jpegBase64 = canvas.toDataURL('image/jpeg', 0.9); // 90% quality
        const webpBase64 = canvas.toDataURL('image/jpeg');
        resolve(webpBase64);
      };

      img.onerror = (error) => {
        reject(new Error(`Failed to load image: ${error}`));
      };

      img.src = URL.createObjectURL(blob);
    });
  }

  return {
    filterTranscriptContext,
    setFilterTranscriptContext,

    aiMode,
    setAiMode,

    filterHomeSystems,
    setFilterHomeSystems,
    scoreThreshold,
    setScoreThreshold,

    storeTranscriptEmbeddingsLoading,
    storeTranscriptEmbeddingsError,

    storeTranscriptEmbeddingsQueue,
    setStoreTranscriptEmbeddingsQueue,

    speakerTranscript,
    setSpeakerTranscript,

    transcriptSentenceEmbeddingsQueueMapRef,
    transcriptSentenceMapRef,
    classifyTranscriptResultsMapRef,

    filterTranscriptResultsMap,
    setFilterTranscriptResultsMap,

    filterTranscriptPrompt,
    setFilterTranscriptPrompt,
    filterTranscriptResult,
    filterTranscriptLoading,
    filterTranscriptError,

    filterModelList,
    filterModel,
    setFilterModel,

    applyClassifier,
    setApplyClassifier,

    homeSystemOptions,
    selectedClassifierOptions,
    setSelectedClassifierOptions,

    classifyTranscriptResult,
    classifyTranscriptLoading,
    classifyTranscriptError,

    filterTranscriptWithDecoder,
    isFilteringTranscript,
    errorFilteringTranscript,
    setErrorFilteringTranscript,

    storeFilterResults,
    filterTranscriptResultsStore,
    setFilterTranscriptResultsStore,

    getImageAsBase64,

    modelSelect,
    setModelSelect,
    modelList,
    analyzeImage,
  };
};

export default useCopilot;
